import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldAlert, Loader2, ChevronLeft, ChevronRight, Trash2, FileSpreadsheet, ChevronDown } from "lucide-react";
import { API, fmtDate, downloadXlsx, todayStr } from "./shared";

const EVENT_LABELS = {
  rate_limit: "Prekročený limit požiadaviek",
  turnstile_failed: "Zlyhanie Turnstile",
  honeypot: "Honeypot (bot)",
  too_fast: "Príliš rýchle odoslanie",
  blocked_upload: "Zablokovaný upload",
  login_failed: "Neúspešné prihlásenie",
  login_locked: "Zablokované prihlásenie",
  forgot_password_blocked: "Blokovaná obnova hesla",
  password_reset: "Obnovenie hesla",
  password_changed: "Zmena hesla",
};

const RESULT_CLS = {
  blocked: "bg-red-500/15 text-red-300 border-red-500/30",
  rejected: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  success: "bg-green-500/15 text-green-300 border-green-500/30",
};

export default function SecurityLog({ token }) {
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const pageSize = 50;

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/security-events`, { ...authHeader, params: { page, page_size: pageSize } })
      .then(({ data }) => { setItems(data.items); setTotal(data.total); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const toggleId = (id) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = items.length > 0 && items.every((it) => selectedIds.has(it.id));
  const toggleAll = () => setSelectedIds((s) => {
    const n = new Set(s);
    if (allSelected) items.forEach((it) => n.delete(it.id)); else items.forEach((it) => n.add(it.id));
    return n;
  });

  const deleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Naozaj chcete odstrániť ${ids.length} označených záznamov? Táto akcia je nevratná.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/security-events/delete`, { ids }, authHeader);
      setSelectedIds(new Set());
      toast.success("Označené záznamy zmazané");
      load();
    } catch { toast.error("Mazanie zlyhalo."); }
    finally { setBusy(false); }
  };

  const deleteAll = async () => {
    if (!window.confirm("Naozaj chcete odstrániť všetky záznamy bezpečnostného denníka? Táto akcia je nevratná.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/security-events/delete`, { all: true }, authHeader);
      setSelectedIds(new Set());
      toast.success("Bezpečnostný denník vymazaný");
      setPage(1); load();
    } catch { toast.error("Mazanie zlyhalo."); }
    finally { setBusy(false); }
  };

  const exportXlsx = async (mode) => {
    setExportOpen(false);
    const params = {};
    if (mode === "selected") {
      if (!selectedIds.size) { toast.error("Nie sú vybraté žiadne záznamy na export."); return; }
      params.ids = [...selectedIds].join(",");
    }
    try {
      await downloadXlsx(axios, `${API}/admin/security-events/export`, params, token, `security-log-export-${todayStr()}.xlsx`);
      toast.success("Export bol vytvorený.");
    } catch { toast.error("Export zlyhal."); }
  };

  return (
    <div data-testid="security-log-panel">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-head text-2xl font-semibold mb-1">Bezpečnostný denník</h1>
          <p className="text-white/60 text-sm">Bezpečnostné udalosti sa automaticky mažú po 30 dňoch.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <div className="relative">
            <button data-testid="security-export-btn" onClick={() => setExportOpen((o) => !o)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white/5 border border-white/10 text-white hover:bg-white/10 text-sm">
              <FileSpreadsheet className="w-4 h-4 text-green-300" /> Export <ChevronDown className="w-4 h-4" />
            </button>
            {exportOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
                <div className="absolute right-0 mt-1 z-20 w-52 bg-[#0d1420] border border-white/15 rounded-md shadow-xl py-1 text-sm">
                  <button data-testid="sec-export-all" onClick={() => exportXlsx("all")} className="block w-full text-left px-4 py-2 hover:bg-white/10">Exportovať všetky</button>
                  <button data-testid="sec-export-selected" onClick={() => exportXlsx("selected")} className="block w-full text-left px-4 py-2 hover:bg-white/10">Exportovať označené ({selectedIds.size})</button>
                </div>
              </>
            )}
          </div>
          <button data-testid="security-delete-all" disabled={busy || total === 0} onClick={deleteAll}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-red-600/90 hover:bg-red-600 text-white text-sm disabled:opacity-50">
            <Trash2 className="w-4 h-4" /> Vymazať celý denník
          </button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4 bg-white/5 border border-white/10 rounded-lg px-4 py-3" data-testid="security-bulk-bar">
          <span className="text-sm text-white/70">Označené: <b className="text-white">{selectedIds.size}</b></span>
          <button data-testid="security-bulk-delete" disabled={busy} onClick={deleteSelected}
            className="ml-auto inline-flex items-center gap-1.5 text-sm bg-red-600/90 hover:bg-red-600 text-white px-3 py-1.5 rounded-md disabled:opacity-60">
            <Trash2 className="w-4 h-4" /> Vymazať označené záznamy
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-white/50"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/15 rounded-xl py-20 text-center text-white/50">
          <ShieldAlert className="w-8 h-8 mx-auto mb-3 text-white/30" />
          Žiadne bezpečnostné udalosti.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-white/60 text-left">
                <tr>
                  <th className="px-4 py-3 w-10"><input data-testid="security-select-all" type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-brand cursor-pointer" /></th>
                  <th className="px-4 py-3 font-medium">Dátum a čas</th>
                  <th className="px-4 py-3 font-medium">Udalosť</th>
                  <th className="px-4 py-3 font-medium">Výsledok</th>
                  <th className="px-4 py-3 font-medium">IP adresa</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Detail</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={it.id} data-testid={`security-row-${i}`} className="border-t border-white/5">
                    <td className="px-4 py-3"><input data-testid={`security-select-${i}`} type="checkbox" checked={selectedIds.has(it.id)} onChange={() => toggleId(it.id)} className="w-4 h-4 accent-brand cursor-pointer" /></td>
                    <td className="px-4 py-3 text-white/70 whitespace-nowrap">{fmtDate(it.created_at)}</td>
                    <td className="px-4 py-3">{EVENT_LABELS[it.event] || it.event}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full border ${RESULT_CLS[it.result] || "bg-white/10 text-white/60 border-white/20"}`}>{it.result || "—"}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-white/60">{it.ip || "—"}</td>
                    <td className="px-4 py-3 text-white/50 hidden lg:table-cell max-w-xs truncate" title={it.user_agent}>{it.detail || it.user_agent || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3 mt-5">
            <span className="text-sm text-white/60">Spolu: {total}</span>
            <div className="flex items-center gap-2">
              <button data-testid="sec-page-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="p-2 rounded-md bg-white/5 border border-white/10 disabled:opacity-40 hover:bg-white/10"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm text-white/70">Strana {page} z {totalPages}</span>
              <button data-testid="sec-page-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="p-2 rounded-md bg-white/5 border border-white/10 disabled:opacity-40 hover:bg-white/10"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
